import React, { useMemo } from "react";
import clsx from "clsx";

import { ITeachingMaterial, ILesson } from "../../../state";
import { isOldMaterial } from "../../../libs/teachingMaterialUtils";

import styles from "./PublishMaterialModal.module.scss";

interface IPublishMaterialModalProps {
  material: ITeachingMaterial;
  calling: boolean;
  onPublishLesson: (lesson_id: number, round_no: number) => void;
  onUnPublishLesson: (lesson_id: number, round_no: number) => void;
  onPublishMaterial: (teaching_material_id: number) => void;
  onUnPublishMaterial: (teaching_material_id: number) => void;
  onClose: () => void;
}

const PublishMaterialModal: React.FC<IPublishMaterialModalProps> = ({
  material,
  calling,
  onPublishLesson,
  onUnPublishLesson,
  onPublishMaterial,
  onUnPublishMaterial,
  onClose,
}) => {
  const lessons = useMemo(
    () =>
      (material.lessons || []).filter(
        l =>
          isOldMaterial(material.mitsuwa_id) || !l.title?.includes("はじめに"),
      ),
    [material.lessons, material.mitsuwa_id],
  );

  const headerName = material.name || "";
  const headerTitle = material.title
    ? material.title.replace(/\s*-高校-|\s*-中学-/g, "")
    : "";

  const publishableLessons = lessons.filter(l => !l.teacher_only);
  const allPublished =
    publishableLessons.length > 0 &&
    publishableLessons.every(l => l.rounds?.every(round => round.published));
  const allUnpublished =
    publishableLessons.length > 0 &&
    publishableLessons.every(l => l.rounds?.every(round => !round.published));

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.container} onClick={e => e.stopPropagation()}>
        <div className={styles.header}>
          <span className={styles.titleText}>
            <span>{headerName}</span>
            <span>{headerTitle}</span>
          </span>
          <div className={styles.bulkActions}>
            <button
              className={clsx(
                styles.bulkPublishBtn,
                allPublished && styles.active,
              )}
              disabled={calling}
              onClick={() => !allPublished && onPublishMaterial(material.id)}
            >
              一括公開
            </button>
            <button
              className={clsx(
                styles.bulkUnpublishBtn,
                allUnpublished && styles.active,
              )}
              disabled={calling}
              onClick={() =>
                !allUnpublished && onUnPublishMaterial(material.id)
              }
            >
              一括非公開
            </button>
          </div>
        </div>
        <div className={styles.body}>
          {lessons.map((lesson: ILesson) =>
            lesson.rounds?.map(round => {
              const lessonTitle =
                lesson.is_practical_worksheet &&
                lesson.rounds &&
                lesson.rounds.length > 1
                  ? `${lesson.title} (${round.round_no})`
                  : lesson.title;
              return (
                <div
                  key={`${lesson.id}-${round.round_no}`}
                  className={styles.lessonRow}
                >
                  <div className={styles.textWrapper}>
                    <div
                      className={clsx(
                        styles.lessonInfo,
                        !round.published && styles.unpublished,
                      )}
                    >
                      {lesson.name?.trim() && (
                        <div className={styles.lessonName}>{lesson.name}</div>
                      )}
                      {lesson.title && (
                        <div className={styles.lessonTitle}>{lessonTitle}</div>
                      )}
                    </div>
                    {!round.published && !lesson.teacher_only && (
                      <div className={styles.unpublishedBadge}>
                        生徒に非公開です
                      </div>
                    )}
                  </div>
                  {!lesson.teacher_only && (
                    <div className={styles.publishControls}>
                      <button
                        className={clsx(
                          styles.publishBtn,
                          round.published && styles.active,
                        )}
                        onClick={() =>
                          !round.published &&
                          onPublishLesson(lesson.id, round.round_no)
                        }
                        disabled={calling}
                      >
                        公開する
                      </button>
                      <button
                        className={clsx(
                          styles.unpublishBtn,
                          !round.published && styles.active,
                        )}
                        onClick={() =>
                          round.published &&
                          onUnPublishLesson(lesson.id, round.round_no)
                        }
                        disabled={calling}
                      >
                        公開しない
                      </button>
                    </div>
                  )}
                </div>
              );
            }),
          )}
        </div>
      </div>
    </div>
  );
};

export default PublishMaterialModal;
